import { useState, useEffect, useCallback, useRef } from "react";
import { useInfiniteScroll } from "./useInfiniteScroll";

function normalizePagination(pagination, page, itemCount) {
  if (!pagination) {
    return {
      currentPage: page,
      totalPages: 1,
      totalItems: itemCount,
      hasNextPage: false,
      hasPrevPage: page > 1,
    };
  }
  return {
    currentPage: pagination.currentPage ?? page,
    totalPages: pagination.totalPages ?? 1,
    totalItems:
      pagination.totalItems ?? pagination.totalPoems ?? itemCount,
    hasNextPage: Boolean(pagination.hasNextPage),
    hasPrevPage: Boolean(pagination.hasPrevPage),
  };
}

export function usePaginatedTab({
  fetchPage,
  enabled = true,
  pageSize = 10,
  resetKey,
  scrollRootSelectors,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const sentinelRef = useRef(null);
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  const load = useCallback(
    async (page, append) => {
      try {
        if (page === 1) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        const result = await fetchPageRef.current(page, pageSize);
        const newItems = result?.items || [];
        setItems((prev) => (append ? [...prev, ...newItems] : newItems));
        setPagination(
          normalizePagination(result?.pagination, page, newItems.length)
        );
      } catch (err) {
        console.error("Failed to load tab page:", err);
        if (!append) setItems([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [pageSize]
  );

  useEffect(() => {
    if (!enabled) return undefined;
    setItems([]);
    setPagination({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      hasNextPage: false,
      hasPrevPage: false,
    });
    load(1, false);
  }, [enabled, resetKey, load]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !pagination.hasNextPage) return;
    load(pagination.currentPage + 1, true);
  }, [loading, loadingMore, pagination, load]);

  useInfiniteScroll({
    enabled,
    hasNextPage: pagination.hasNextPage,
    isLoading: loading,
    isLoadingMore: loadingMore,
    onLoadMore: loadMore,
    sentinelRef,
    scrollRootSelectors: scrollRootSelectors ?? [
      ".home-page__feed-column",
      ".group-page__main",
    ],
  });

  const reload = useCallback(() => load(1, false), [load]);

  return {
    items,
    setItems,
    loading,
    loadingMore,
    pagination,
    loadMore,
    sentinelRef,
    reload,
  };
}
